// src/pages/admin/LoginPage.js
import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../services/firebase/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, Loader, AlertCircle } from 'lucide-react';

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // בדוק אם המנהל כבר מחובר
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        navigate('/admin/leads');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/admin/leads');
    } catch (error) {
      console.error('Login error:', error);
      switch (error.code) {
        case 'auth/user-not-found':
          setError('משתמש לא נמצא במערכת');
          break;
        case 'auth/wrong-password':
          setError('סיסמה שגויה');
          break;
        case 'auth/invalid-email':
          setError('כתובת אימייל לא תקינה');
          break;
        case 'auth/user-disabled':
          setError('חשבון המשתמש הושבת');
          break;
        default:
          setError('שגיאה בהתחברות. אנא נסה שוב.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-700 to-slate-800 flex items-center justify-center px-4">
      {/* רקע מעוצב */}
      <div className="absolute inset-0 opacity-40">
        <svg width="60" height="60" viewBox="0 0 60 60" className="w-full h-full">
          <defs>
            <pattern id="dots" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <circle cx="30" cy="30" r="2" fill="white" fillOpacity="0.05"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)"/>
        </svg>
      </div>

      {/* כרטיס התחברות */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20">
          {/* כותרת */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">התחברות מנהל</h1>
            <p className="text-white/70 text-sm"> גמלנט AI - ממשק ניהול</p>
          </div>

          {/* טופס התחברות */}
          <form onSubmit={handleLogin} className="space-y-6">
            {/* שגיאה */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <span className="text-red-200 text-sm">{error}</span>
              </div>
            )}

            {/* אימייל */}
            <div className="relative">
              <label className="block text-sm font-medium text-white/90 mb-2">
                אימייל
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-white/60" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-4 pr-10 py-3 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all duration-300 text-white placeholder-white/60"
                  placeholder="admin@example.com"
                  dir="ltr"
                />
              </div>
            </div>

            {/* סיסמה */}
            <div className="relative">
              <label className="block text-sm font-medium text-white/90 mb-2">
                סיסמה
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-white/60" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-10 py-3 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all duration-300 text-white placeholder-white/60"
                  placeholder="••••••••"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 left-0 pl-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-white/60 hover:text-white/90" />
                  ) : (
                    <Eye className="h-5 w-5 text-white/60 hover:text-white/90" />
                  )}
                </button>
              </div>
            </div>

            {/* כפתור התחברות */}
            <button
              type="submit"
              disabled={loading}
              className={`
                w-full py-3 px-4 rounded-xl text-lg font-bold
                flex items-center justify-center gap-3
                ${loading 
                  ? 'bg-gray-500/50 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-blue-500 via-purple-600 to-pink-600 hover:from-blue-600 hover:via-purple-700 hover:to-pink-700'
                }
                text-white transition-all duration-300
                transform hover:scale-105 active:scale-95
                shadow-xl hover:shadow-2xl
                border border-white/20
              `}
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>מתחבר...</span>
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  <span>התחבר</span>
                </>
              )}
            </button>
          </form>

          {/* פוטר */}
          <div className="mt-8 pt-6 border-t border-white/20 text-center">
            <p className="text-white/60 text-xs">
              ממשק ניהול מוגן - גישה למנהלים בלבד
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;